/**
 * UI Context - Backward Compatibility Layer
 *
 * This file re-exports from the Zustand store for backward compatibility.
 * New code should import directly from '../stores/uiStore'.
 *
 * Migration: UIContext -> Zustand
 * - No Provider needed (Zustand manages state globally)
 * - Same hook API, just different underlying implementation
 */

import { useEffect } from 'react';
import {
  useUIStore,
  useTheme as useZustandTheme,
  useSetTheme,
  useModals as useZustandModals,
  useOpenModal,
  useCloseModal,
} from '../stores/uiStore';

/**
 * Main UI hook - provides all UI state
 * For better performance, use specific hooks like useModals, useTheme, etc.
 */
export function useUI() {
  const store = useUIStore();
  return {
    currentView: store.currentView,
    setCurrentView: store.setCurrentView,
    isMobile: store.isMobile,
    setIsMobile: store.setIsMobile,
    modals: store.modals,
    openModal: store.openModal,
    closeModal: store.closeModal,
    closeAllModals: store.closeAllModals,
    theme: store.theme,
    setTheme: store.setTheme,
  };
}

/**
 * Modal state hook
 */
export function useModals() {
  const modals = useZustandModals();
  const openModal = useOpenModal();
  const closeModal = useCloseModal();
  const closeAllModals = useUIStore((s) => s.closeAllModals);
  return { modals, openModal, closeModal, closeAllModals };
}

/**
 * Theme hook
 */
export function useTheme() {
  const theme = useZustandTheme();
  const setTheme = useSetTheme();
  return { theme, setTheme };
}

/**
 * UIProvider - No-op for backward compatibility
 * Zustand doesn't need a provider, but this allows existing App.tsx to work
 */
export function UIProvider({ children }: { children: React.ReactNode }) {
  const setIsMobile = useUIStore((s) => s.setIsMobile);

  // Initialize mobile detection
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, [setIsMobile]);

  return <>{children}</>;
}

export default { useUI, useModals, useTheme, UIProvider };
